// BA-tauglicher 4-Schritte-Wizard für die Mapping-Pflege.
// Ersetzt den alten MappingEditDialog: statt rohem JSON führt der Wizard den
// Business Analyst über (1) EDI-Quelle, (2) BO4E-Ziel, (3) Transform,
// (4) Prüfen & Speichern. Eigener Stepper statt ui5 <Wizard> — der ist im
// Dialog-Kontext zickig.
//
// Zwei Modi: "edit" (default, PUT auf bestehendes Entry) und "create"
// (POST eines neuen Entries; optional mit presetAnchor aus der Struktur-
// Ansicht vorausgewählt — dann startet der Wizard direkt auf Schritt 2).

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Modal,
  Button,
  Select,
  SelectItem,
  Notice,
  Checkbox,
  RadioGroup,
  Radio,
  Disclosure,
  Icon,
  Badge,
} from "@conuti-das/prince-ui";
import { Flex, Stack, princeIcon } from "@/ui-prince";
import {
  mapperApi,
  VersionConflictError,
  type Bo4eFieldInfo,
  type Bo4eTarget,
  type EdiAnchor,
  type MappingEntry,
  type EntryUpdatePayload,
  type EntryCreatePayload,
} from "@/api/mapperApi";
import {
  anchorDetailLine,
  anchorKey,
  CORE_TRANSFORMS,
  DATE_FORMATS,
  describeTransform,
  filterAnchors,
  filterTargets,
  findInitialAnchor,
  findInitialTarget,
  initialDateFormat,
  isCoreTransformType,
  lookupRowsFromTable,
  lookupTableFromRows,
  type CoreTransformType,
  type LookupRow,
} from "./wizardLogic";
import { MappingFlowHeader } from "./MappingFlowHeader";
import { EdiVerortung } from "./EdiVerortung";
import { Bo4eZielDoc } from "./Bo4eZielDoc";

const STATUS_VALUES = ["draft", "approved", "needs_review"] as const;
const DIRECTIONS: Array<{ value: string; label: string }> = [
  { value: "both", label: "beide Richtungen" },
  { value: "inbound", label: "nur EDI → BO4E (inbound)" },
  { value: "outbound", label: "nur BO4E → EDI (outbound)" },
];

const STEPS = [
  { n: 1, title: "EDI-Quelle wählen" },
  { n: 2, title: "BO4E-Ziel wählen" },
  { n: 3, title: "Transform festlegen" },
  { n: 4, title: "Prüfen & Speichern" },
] as const;

/** Max. gerenderte Listen-Treffer — darüber bitten wir um weiteres Eingrenzen. */
const MAX_LIST_ROWS = 150;

type TargetChoice =
  | { kind: "skeleton"; target: Bo4eTarget }
  | { kind: "current" }; // Excel-Altlast behalten (nicht im Skelett)

type Props = {
  bundleId: string;
  msgType: string;
  /** Bestehendes Entry (edit) — bei mode "create" darf es fehlen. */
  entry?: MappingEntry | null;
  ediAnchors: EdiAnchor[];
  bo4eTargets: Bo4eTarget[];
  /** "edit" (default): PUT auf entry. "create": POST eines neuen Entries. */
  mode?: "edit" | "create";
  /** Vorausgewählter EDI-Anker (create aus der Struktur-Ansicht) —
   * der Wizard startet dann direkt auf Schritt 2. */
  presetAnchor?: EdiAnchor;
  /** Vorausgewähltes BO4E-Ziel (create aus der BO-Perspektive) —
   * das EDI-Quell-Feld fehlt noch, also startet der Wizard auf Schritt 1. */
  presetBo4e?: { bo: string; path: string };
  onClose: () => void;
  /** Nach erfolgreichem Speichern: Tabelle + Coverage neu laden. */
  onSaved: () => void;
};

/** Leeres Basis-Entry für den Create-Modus — hält die Wizard-Initialisierung
 * (Transform, Richtung, Status …) identisch zu edit. */
function emptyEntry(msgType: string, presetAnchor?: EdiAnchor): MappingEntry {
  return {
    mapping_id: "",
    version: 0,
    edi_anchor: presetAnchor ?? { message_type: msgType, segment: "" },
    bo4e_anchor: { bo: "", path: "" },
    transform: { type: "identity" },
    direction: "both",
    status: "approved",
    notes: "",
  };
}

// ---- kleine Render-Helfer -------------------------------------------------

const rowBase: CSSProperties = {
  padding: "var(--s-2) var(--s-3)",
  cursor: "pointer",
  borderBottom: "1px solid var(--sapList_BorderColor)",
  borderLeft: "3px solid transparent",
};
const rowSelected: CSSProperties = {
  ...rowBase,
  background: "var(--sapList_SelectionBackgroundColor)",
  borderLeft: "3px solid var(--sapList_SelectionBorderColor, var(--sapBrandColor))",
};
const mono: CSSProperties = { fontFamily: "var(--sapFontMonoFamily, monospace)" };
const small: CSSProperties = { fontSize: "0.75rem", color: "var(--sapContent_LabelColor)" };


export function EntryEditWizard({
  bundleId,
  msgType,
  entry,
  ediAnchors,
  bo4eTargets,
  mode: wizardMode = "edit",
  presetAnchor,
  presetBo4e,
  onClose,
  onSaved,
}: Props) {
  const isCreate = wizardMode === "create";
  // Basis-Entry: bei edit das übergebene Entry, bei create ein leeres Gerüst —
  // damit bleibt die gesamte Initialisierung unten modus-unabhängig.
  const base = useMemo(
    () => entry ?? emptyEntry(msgType, presetAnchor),
    [entry, msgType, presetAnchor],
  );

  // create mit presetAnchor: EDI-Quelle steht schon fest → direkt Schritt 2.
  // create mit presetBo4e: BO4E-Ziel steht fest, EDI-Quelle fehlt → Schritt 1.
  const [step, setStep] = useState(isCreate && presetAnchor ? 2 : 1);

  // ---- Schritt 1: EDI-Quelle ----
  const [anchorQuery, setAnchorQuery] = useState("");
  const [allSegments, setAllSegments] = useState(false);
  const [selectedAnchor, setSelectedAnchor] = useState<EdiAnchor | null>(() =>
    isCreate ? (presetAnchor ?? null) : findInitialAnchor(ediAnchors, base.edi_anchor),
  );

  // ---- Schritt 2: BO4E-Ziel ----
  const currentInSkeleton = useMemo(
    () => (isCreate ? null : findInitialTarget(bo4eTargets, base.bo4e_anchor)),
    [isCreate, bo4eTargets, base.bo4e_anchor],
  );
  // create aus der BO-Perspektive: BO4E-Ziel ist vorgegeben — bevorzugt das
  // Skelett-Target (mit Typ/enum), sonst ein minimales Target aus {bo,path}.
  const presetTarget = useMemo<Bo4eTarget | null>(() => {
    if (!isCreate || !presetBo4e) return null;
    return findInitialTarget(bo4eTargets, presetBo4e) ?? { bo: presetBo4e.bo, path: presetBo4e.path };
  }, [isCreate, presetBo4e, bo4eTargets]);
  const [targetQuery, setTargetQuery] = useState("");
  const [targetChoice, setTargetChoice] = useState<TargetChoice | null>(() =>
    currentInSkeleton
      ? { kind: "skeleton", target: currentInSkeleton }
      : presetTarget
        ? { kind: "skeleton", target: presetTarget }
        : isCreate
          ? null // create: Ziel muss aktiv aus dem Skelett gewählt werden
          : { kind: "current" },
  );

  // ---- Schritt 3: Transform ----
  const initialMode: CoreTransformType | "advanced" = isCoreTransformType(base.transform.type)
    ? base.transform.type
    : "advanced";
  const [mode, setMode] = useState<CoreTransformType | "advanced">(initialMode);
  const [advancedOpen, setAdvancedOpen] = useState(initialMode === "advanced");
  const [advancedJson, setAdvancedJson] = useState(() =>
    JSON.stringify(base.transform, null, 2),
  );
  const [rows, setRows] = useState<LookupRow[]>(() => lookupRowsFromTable(base.transform.table));
  const [dateFormat, setDateFormat] = useState(() =>
    initialDateFormat(
      isCreate ? (presetAnchor ?? null) : findInitialAnchor(ediAnchors, base.edi_anchor),
      base.transform.edi_qualifier,
    ),
  );
  const [constValue, setConstValue] = useState(base.transform.value ?? "");
  const [direction, setDirection] = useState(base.direction || "both");

  // ---- Schritt 4: Status / Notizen ----
  // Wizard-Durchlauf = der BA hat gerade disambiguiert → needs_review wird
  // standardmäßig zu approved vorgeschlagen (create startet ohnehin approved).
  const [status, setStatus] = useState(
    base.status === "needs_review" ? "approved" : base.status,
  );
  const [notes, setNotes] = useState(base.notes ?? "");

  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // KI-Assistenz (Teilprojekt B): Vorschlag holen + als Vorauswahl übernehmen.
  const [assisting, setAssisting] = useState(false);
  const [assistNote, setAssistNote] = useState<string | null>(null);

  // ---- abgeleitete Daten ----
  // Segment-Vorfilter: Segment des Basis-Ankers; leer (create ohne preset) = alle.
  const segmentFilter = base.edi_anchor.segment || null;
  const filteredAnchors = useMemo(
    () => filterAnchors(ediAnchors, anchorQuery, allSegments ? null : segmentFilter),
    [ediAnchors, anchorQuery, allSegments, segmentFilter],
  );
  const filteredTargets = useMemo(
    () => filterTargets(bo4eTargets, targetQuery),
    [bo4eTargets, targetQuery],
  );
  const lookupTable = useMemo(() => lookupTableFromRows(rows), [rows]);
  // BO4E-Schema-Doku des gewählten Ziels (Beschreibung + Enum-Werte), lazy geladen.
  const [bo4eFieldInfo, setBo4eFieldInfo] = useState<Bo4eFieldInfo | null>(null);
  // Enum-Quelle vereinheitlicht: Skelett-Target-Enum, sonst aufgelöstes Schema-Enum —
  // damit gibt es das BO4E-Wert-Dropdown auch für Nicht-Skelett-Ziele (Excel-Altlast).
  const targetEnum: string[] =
    targetChoice?.kind === "skeleton" && targetChoice.target.enum?.length
      ? targetChoice.target.enum
      : (bo4eFieldInfo?.found ? bo4eFieldInfo.enum ?? [] : []);
  const anchorPool: string[] = selectedAnchor?.value_pool ?? [];

  function parseAdvanced(): Record<string, unknown> | null {
    try {
      const o = JSON.parse(advancedJson) as Record<string, unknown>;
      if (o && typeof o === "object" && typeof o.type === "string") return o;
      return null;
    } catch {
      return null;
    }
  }

  function isStepValid(n: number): boolean {
    if (n === 1) return selectedAnchor !== null;
    if (n === 2) return targetChoice !== null;
    if (n === 3) {
      if (mode === "identity") return true;
      if (mode === "code_lookup") return Object.keys(lookupTable).length > 0;
      if (mode === "date_format") return !!dateFormat;
      if (mode === "const") return constValue.trim() !== "";
      return parseAdvanced() !== null;
    }
    return true;
  }
  /** Schritt n erreichbar, wenn alle vorherigen Schritte gültig sind. */
  function canReach(n: number): boolean {
    for (let k = 1; k < n; k++) if (!isStepValid(k)) return false;
    return true;
  }

  const effectiveBo4e =
    targetChoice?.kind === "skeleton"
      ? { bo: targetChoice.target.bo, path: targetChoice.target.path }
      : base.bo4e_anchor;

  // Schema-Doku für das aktuell wirksame BO4E-Ziel nachladen (abbrechbar).
  // Fehler/`found:false` → stiller Fallback (kein Dropdown, keine Doku).
  useEffect(() => {
    const { bo, path } = effectiveBo4e;
    if (!bo || !path) {
      setBo4eFieldInfo(null);
      return;
    }
    let cancelled = false;
    mapperApi
      .getBo4eField(bo, path)
      .then((info) => {
        if (!cancelled) setBo4eFieldInfo(info);
      })
      .catch(() => {
        if (!cancelled) setBo4eFieldInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveBo4e.bo, effectiveBo4e.path]);

  function buildTransform(): Record<string, unknown> | null {
    if (mode === "identity") return { type: "identity" };
    if (mode === "code_lookup") return { type: "code_lookup", table: lookupTable };
    if (mode === "date_format")
      return { type: "date_format", edi_qualifier: dateFormat, target: "iso8601" };
    if (mode === "const") return { type: "const", value: constValue };
    return parseAdvanced();
  }

  async function save() {
    setError(null);
    setConflict(false);
    const transform = buildTransform();
    if (!transform) {
      setError("Transform-JSON ist nicht parsebar oder hat kein \"type\"-Feld.");
      return;
    }
    if (!selectedAnchor) return; // Schritt-Gating verhindert das; defensiv.
    setSaving(true);
    try {
      if (isCreate) {
        const body: EntryCreatePayload = {
          edi_anchor: { ...selectedAnchor } as Record<string, unknown>,
          bo4e_anchor: effectiveBo4e,
          transform,
          direction,
          notes,
          status,
        };
        await mapperApi.createEntry(msgType, body, bundleId);
      } else {
        const body: EntryUpdatePayload = {
          version: base.version,
          edi_anchor: { ...selectedAnchor } as Record<string, unknown>,
          bo4e_anchor: effectiveBo4e,
          transform,
          direction,
          notes,
          status,
        };
        await mapperApi.updateEntry(msgType, base.mapping_id, body, bundleId);
      }
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof VersionConflictError) setConflict(true);
      else setError(String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm("Mapping wirklich löschen?")) return;
    setError(null);
    setConflict(false);
    setDeleting(true);
    try {
      await mapperApi.deleteEntry(msgType, base.mapping_id, bundleId, base.version);
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof VersionConflictError) setConflict(true);
      else setError(String(e));
    } finally {
      setDeleting(false);
    }
  }

  // KI-Vorschlag auf den Wizard-State anwenden (nur Vorauswahl — Nutzer prüft/speichert).
  function applySuggestedTransform(t: Record<string, unknown> | undefined) {
    if (!t || typeof t.type !== "string") return;
    const type = t.type;
    if (type === "identity") {
      setMode("identity");
    } else if (type === "code_lookup") {
      setMode("code_lookup");
      setRows(lookupRowsFromTable((t.table as Record<string, string>) ?? null));
    } else if (type === "date_format") {
      setMode("date_format");
      if (typeof t.edi_qualifier === "string") setDateFormat(t.edi_qualifier);
    } else if (type === "const") {
      setMode("const");
      setConstValue(typeof t.value === "string" ? t.value : String(t.value ?? ""));
    } else {
      setMode("advanced");
      setAdvancedOpen(true);
      setAdvancedJson(JSON.stringify(t, null, 2));
    }
  }

  async function handleAssist() {
    setError(null);
    setAssistNote(null);
    setAssisting(true);
    const want: string[] = [];
    if (!selectedAnchor) want.push("source");
    if (!targetChoice) want.push("target");
    want.push("transform");
    try {
      const res = await mapperApi.suggestEntry({
        message_type: msgType,
        bundle: bundleId,
        edi_anchor: selectedAnchor ?? undefined,
        bo4e_anchor: effectiveBo4e.bo ? effectiveBo4e : undefined,
        transform: buildTransform() ?? undefined,
        direction,
        want,
      });
      const s = res.suggestions ?? {};
      if (s.edi_anchor) {
        const match = findInitialAnchor(ediAnchors, s.edi_anchor as unknown as EdiAnchor);
        setSelectedAnchor(match ?? (s.edi_anchor as unknown as EdiAnchor));
      }
      if (s.bo4e_anchor?.bo && s.bo4e_anchor?.path) {
        const t =
          findInitialTarget(bo4eTargets, s.bo4e_anchor) ??
          { bo: s.bo4e_anchor.bo, path: s.bo4e_anchor.path };
        setTargetChoice({ kind: "skeleton", target: t });
      }
      if (s.transform) applySuggestedTransform(s.transform);
      if (typeof s.direction === "string") setDirection(s.direction);
      setAssistNote(res.rationale || "KI-Vorschlag übernommen — bitte prüfen.");
    } catch (e) {
      setError("KI-Assistenz nicht verfügbar: " + String(e));
    } finally {
      setAssisting(false);
    }
  }

  // ---- Schritt-Inhalte ----

  function renderAnchorRow(a: EdiAnchor) {
    const sel = selectedAnchor !== null && anchorKey(selectedAnchor) === anchorKey(a);
    return (
      <div
        key={anchorKey(a)}
        style={sel ? rowSelected : rowBase}
        onClick={() => setSelectedAnchor(a)}
        data-testid={`anchor-row-${anchorKey(a)}`}
        role="option"
        aria-selected={sel}
      >
        <span style={{ fontWeight: "bold" }}>{a.seg_name || a.segment}</span>
        <span style={{ ...mono, display: "block" }}>{anchorDetailLine(a)}</span>
        {(a.example || (a.value_pool && a.value_pool.length > 0)) && (
          <span style={{ ...small, display: "block" }}>
            {a.example ? `Beispiel: ${a.example}` : ""}
            {a.example && a.value_pool && a.value_pool.length > 0 ? "  ·  " : ""}
            {a.value_pool && a.value_pool.length > 0
              ? `Codes: ${a.value_pool.slice(0, 6).join(", ")}${a.value_pool.length > 6 ? ", …" : ""}`
              : ""}
          </span>
        )}
      </div>
    );
  }

  function step1() {
    return (
      <Stack style={{ gap: "var(--s-2)" }}>
        <span style={small}>
          Welches Feld der EDIFACT-Nachricht ist die Quelle? Suche über Segment, Qualifier,
          nr, Namen, Beispiel oder Codes.
        </span>
        <Flex style={{ gap: "var(--s-3)", alignItems: "center", flexWrap: "wrap" }}>
          <input
            className="prn-input"
            placeholder="z.B. MP-ID, NAD, 00008, Absender …"
            value={anchorQuery}
            onChange={(e) => setAnchorQuery(e.target.value)}
            style={{ width: "360px" }}
            data-testid="anchor-search"
          />
          {segmentFilter && (
            <Checkbox
              isSelected={allSegments}
              onChange={setAllSegments}
              data-testid="all-segments"
            >
              {`alle Segmente zeigen (sonst nur ${segmentFilter})`}
            </Checkbox>
          )}
          <span style={small}>{filteredAnchors.length} Treffer</span>
        </Flex>
        {ediAnchors.length === 0 && (
          <Notice tone="info">
            Skelett-Anker noch nicht geladen — Dialog schließen und erneut öffnen.
          </Notice>
        )}
        {filteredAnchors.length > MAX_LIST_ROWS && (
          <Notice tone="info">
            {filteredAnchors.length} Treffer — nur die ersten {MAX_LIST_ROWS} werden angezeigt,
            bitte weiter eingrenzen.
          </Notice>
        )}
        <div
          style={{
            maxHeight: "44vh",
            overflow: "auto",
            border: "1px solid var(--sapList_BorderColor)",
            borderRadius: "var(--sapElement_BorderCornerRadius, 4px)",
          }}
          role="listbox"
          aria-label="EDI-Anker"
        >
          {filteredAnchors.slice(0, MAX_LIST_ROWS).map(renderAnchorRow)}
        </div>
      </Stack>
    );
  }

  function renderTargetRow(t: Bo4eTarget) {
    const sel =
      targetChoice?.kind === "skeleton" &&
      targetChoice.target.bo === t.bo &&
      targetChoice.target.path === t.path;
    return (
      <div
        key={`${t.bo}.${t.path}`}
        style={sel ? rowSelected : rowBase}
        onClick={() => setTargetChoice({ kind: "skeleton", target: t })}
        role="option"
        aria-selected={sel}
      >
        <span style={{ ...mono, fontWeight: "bold" }}>{`${t.bo}.${t.path}`}</span>
        <span style={{ ...small, display: "block" }}>
          {`Typ ${t.type ?? "?"}${t.cardinality ? ` · ${t.cardinality}` : ""}`}
          {t.enum && t.enum.length > 0
            ? `  ·  Werte: ${t.enum.slice(0, 6).join(", ")}${t.enum.length > 6 ? ", …" : ""}`
            : ""}
        </span>
      </div>
    );
  }

  function step2() {
    const currentLabel = `${base.bo4e_anchor.bo}.${base.bo4e_anchor.path}`;
    return (
      <Stack style={{ gap: "var(--s-2)" }}>
        <span style={small}>
          In welches BO4E-Feld soll der Wert geschrieben werden?
        </span>
        {!isCreate && !currentInSkeleton && (
          <Notice tone="info">
            Aktuelles Ziel „{currentLabel}" ist kein Skelett-Pfad (Excel-Altlast) —
            eine Auswahl aus der Liste ersetzt es.
          </Notice>
        )}
        <Flex style={{ gap: "var(--s-3)", alignItems: "center" }}>
          <input
            className="prn-input"
            placeholder="z.B. Marktteilnehmer rollencodenummer …"
            value={targetQuery}
            onChange={(e) => setTargetQuery(e.target.value)}
            style={{ width: "360px" }}
            data-testid="target-search"
          />
          <span style={small}>{filteredTargets.length} Treffer</span>
        </Flex>
        {filteredTargets.length > MAX_LIST_ROWS && (
          <Notice tone="info">
            {filteredTargets.length} Treffer — nur die ersten {MAX_LIST_ROWS} werden angezeigt,
            bitte weiter eingrenzen.
          </Notice>
        )}
        <div
          style={{
            maxHeight: "44vh",
            overflow: "auto",
            border: "1px solid var(--sapList_BorderColor)",
            borderRadius: "var(--sapElement_BorderCornerRadius, 4px)",
          }}
          role="listbox"
          aria-label="BO4E-Ziele"
        >
          {!isCreate && !currentInSkeleton && (
            <div
              style={targetChoice?.kind === "current" ? rowSelected : rowBase}
              onClick={() => setTargetChoice({ kind: "current" })}
              role="option"
              aria-selected={targetChoice?.kind === "current"}
              data-testid="keep-current-target"
            >
              <span style={{ fontWeight: "bold" }}>Aktuelles Ziel behalten</span>
              <span style={{ ...mono, display: "block" }}>{currentLabel}</span>
            </div>
          )}
          {filteredTargets.slice(0, MAX_LIST_ROWS).map(renderTargetRow)}
        </div>
        {bo4eFieldInfo && <Bo4eZielDoc info={bo4eFieldInfo} align="left" />}
      </Stack>
    );
  }

  function lookupRowEditor(row: LookupRow, idx: number) {
    const setRow = (patch: Partial<LookupRow>) =>
      setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const ediOptions = anchorPool.includes(row.edi) || row.edi === ""
      ? anchorPool
      : [row.edi, ...anchorPool];
    const boOptions = targetEnum.includes(row.bo4e) || row.bo4e === ""
      ? targetEnum
      : [row.bo4e, ...targetEnum];
    return (
      <Flex key={idx} style={{ gap: "var(--s-2)", alignItems: "center" }}>
        {anchorPool.length > 0 ? (
          <Select
            style={{ width: "200px" }}
            selectedKey={row.edi || "__empty__"}
            onSelectionChange={(k) => setRow({ edi: k === "__empty__" ? "" : String(k) })}
          >
            <SelectItem id="__empty__">— EDI-Code —</SelectItem>
            {ediOptions.map((c) => (
              <SelectItem key={c} id={c}>{c}</SelectItem>
            ))}
          </Select>
        ) : (
          <input
            className="prn-input"
            placeholder="EDI-Code"
            value={row.edi}
            onChange={(e) => setRow({ edi: e.target.value })}
            style={{ width: "200px" }}
          />
        )}
        <span>→</span>
        {targetEnum.length > 0 ? (
          <Select
            style={{ width: "280px" }}
            selectedKey={row.bo4e || "__empty__"}
            onSelectionChange={(k) => setRow({ bo4e: k === "__empty__" ? "" : String(k) })}
          >
            <SelectItem id="__empty__">— BO4E-Wert —</SelectItem>
            {boOptions.map((c) => (
              <SelectItem key={c} id={c}>{c}</SelectItem>
            ))}
          </Select>
        ) : (
          <input
            className="prn-input"
            placeholder="BO4E-Wert"
            value={row.bo4e}
            onChange={(e) => setRow({ bo4e: e.target.value })}
            style={{ width: "280px" }}
          />
        )}
        <Button
          variant="plain"
          onPress={() =>
            setRows((rs) => (rs.length > 1 ? rs.filter((_, i) => i !== idx) : [{ edi: "", bo4e: "" }]))
          }
        >
          <Icon name={princeIcon("decline")} size={16} />
        </Button>
      </Flex>
    );
  }

  function step3() {
    return (
      <Stack style={{ gap: "var(--s-3)" }}>
        <span style={small}>Wie wird der EDI-Wert in den BO4E-Wert überführt?</span>
        <RadioGroup
          value={mode}
          onChange={(v) => {
            setMode(v as CoreTransformType | "advanced");
            if (v === "date_format") {
              setDateFormat(initialDateFormat(selectedAnchor, base.transform.edi_qualifier));
            }
          }}
        >
          {CORE_TRANSFORMS.map((t) => (
            <Flex key={t.type} style={{ alignItems: "center", gap: "var(--s-2)" }}>
              <Radio value={t.type} data-testid={`transform-${t.type}`}>{t.label}</Radio>
              <span style={small}>{t.hint}</span>
            </Flex>
          ))}
        </RadioGroup>

        {mode === "code_lookup" && (
          <Stack style={{ gap: "var(--s-2)", paddingLeft: "var(--s-4)" }}>
            <label className="prn-field-label">Code-Tabelle (EDI-Code → BO4E-Wert)</label>
            {rows.map(lookupRowEditor)}
            <Flex style={{ gap: "var(--s-2)" }}>
              <Button variant="plain" onPress={() => setRows((rs) => [...rs, { edi: "", bo4e: "" }])}>
                <Icon name={princeIcon("add")} size={16} />
                Zeile hinzufügen
              </Button>
              {anchorPool.length > 0 && (
                <Button
                  variant="plain"
                  onPress={() =>
                    setRows(
                      anchorPool.map((c) => ({ edi: c, bo4e: lookupTable[c] ?? "" })),
                    )
                  }
                >
                  alle Pool-Codes übernehmen ({anchorPool.length})
                </Button>
              )}
            </Flex>
            {Object.keys(lookupTable).length === 0 && (
              <span style={{ fontSize: "0.75rem", color: "var(--sapCriticalTextColor)" }}>
                Mindestens ein vollständiges Code-Paar wird benötigt.
              </span>
            )}
          </Stack>
        )}

        {mode === "date_format" && (
          <Flex style={{ gap: "var(--s-4)", paddingLeft: "var(--s-4)", alignItems: "flex-end", flexWrap: "wrap" }}>
            <Stack style={{ gap: "var(--s-1)" }}>
              <label className="prn-field-label" htmlFor="date-format-select">EDIFACT-Format (DE2379)</label>
              <Select
                id="date-format-select"
                selectedKey={dateFormat || "__none__"}
                onSelectionChange={(k) => { if (k && k !== "__none__") setDateFormat(String(k)); }}
              >
                {DATE_FORMATS.map((f) => (
                  <SelectItem key={f.code} id={f.code}>{f.label}</SelectItem>
                ))}
              </Select>
            </Stack>
            <Stack style={{ gap: "var(--s-1)" }}>
              <label className="prn-field-label">Ziel-Format (fix)</label>
              <input className="prn-input" value="iso8601" readOnly style={{ width: "140px" }} />
            </Stack>
          </Flex>
        )}

        {mode === "const" && (
          <Stack style={{ gap: "var(--s-1)", paddingLeft: "var(--s-4)" }}>
            <label className="prn-field-label" htmlFor="const-value">
              {targetEnum.length > 0 ? "Konstanter Wert (BO4E-Enum)" : "Konstanter Wert"}
            </label>
            {targetEnum.length > 0 ? (
              <Select
                id="const-value"
                data-testid="const-value-select"
                style={{ width: "320px" }}
                selectedKey={constValue || "__empty__"}
                onSelectionChange={(k) => { if (k != null) setConstValue(k === "__empty__" ? "" : String(k)); }}
              >
                <SelectItem id="__empty__">— Wert wählen —</SelectItem>
                {(constValue && !targetEnum.includes(constValue)
                  ? [constValue, ...targetEnum]
                  : targetEnum
                ).map((c) => (
                  <SelectItem key={c} id={c}>{c}</SelectItem>
                ))}
              </Select>
            ) : (
              <input
                id="const-value"
                className="prn-input"
                value={constValue}
                onChange={(e) => setConstValue(e.target.value)}
                style={{ width: "320px" }}
                data-testid="const-value"
              />
            )}
          </Stack>
        )}

        <Disclosure
          title="Erweitert (JSON) — concat / split / conditional / manual"
          isExpanded={advancedOpen}
          onExpandedChange={setAdvancedOpen}
        >
          <Stack style={{ gap: "var(--s-2)" }}>
            <RadioGroup
              value={mode}
              onChange={(v) => setMode(v as CoreTransformType | "advanced")}
            >
              <Radio value="advanced" data-testid="transform-advanced">
                Erweitertes JSON verwenden (überschreibt die Auswahl oben)
              </Radio>
            </RadioGroup>
            {base.transform.type === "manual" && base.transform.raw && (
              <Notice tone="info">
                Original-Excel-Regel: {base.transform.raw}
              </Notice>
            )}
            <textarea
              className="prn-input"
              rows={8}
              value={advancedJson}
              onChange={(e) => setAdvancedJson(e.target.value)}
              style={{ ...mono, width: "100%" }}
              data-testid="advanced-json"
            />
            {mode === "advanced" && parseAdvanced() === null && (
              <span style={{ fontSize: "0.75rem", color: "var(--sapCriticalTextColor)" }}>
                JSON nicht parsebar oder ohne "type"-Feld.
              </span>
            )}
          </Stack>
        </Disclosure>

        <Stack style={{ gap: "var(--s-1)" }}>
          <label className="prn-field-label">Richtung</label>
          <RadioGroup value={direction} onChange={setDirection}>
            <Flex style={{ gap: "var(--s-3)", flexWrap: "wrap" }}>
              {DIRECTIONS.map((d) => (
                <Radio key={d.value} value={d.value}>{d.label}</Radio>
              ))}
            </Flex>
          </RadioGroup>
        </Stack>
      </Stack>
    );
  }

  function step4() {
    const a = selectedAnchor;
    const transformText = describeTransform(mode, {
      table: lookupTable,
      dateFormat,
      constValue,
      advancedType: String(parseAdvanced()?.type ?? base.transform.type),
    });
    const dirLabel = DIRECTIONS.find((d) => d.value === direction)?.label ?? direction;
    return (
      <Stack style={{ gap: "var(--s-3)" }}>
        {a && (
          <span data-testid="summary-sentence">
            Aus dem EDI-Segment <strong>{a.seg_name || a.segment}</strong> (
            {a.segment}
            {a.qualifier ? `+${a.qualifier}` : ""}
            {a.nr ? `, nr ${a.nr}` : ""}) wird{" "}
            {a.data_element ? (
              <>
                das Datenelement{" "}
                <strong>
                  DE{a.data_element}
                  {a.de_name ? ` ${a.de_name}` : ""}
                </strong>{" "}
              </>
            ) : (
              <>der Segmentwert </>
            )}
            gelesen, {transformText} und nach{" "}
            <strong>{`${effectiveBo4e.bo}.${effectiveBo4e.path}`}</strong> geschrieben.
            {" "}Richtung: {dirLabel}.
          </span>
        )}
        {a?.example && (
          <span style={small}>
            Beispiel aus dem MIG: <span style={mono}>{a.example}</span>
          </span>
        )}

        {a && (
          <Stack style={{ gap: "var(--s-1)" }}>
            <label className="prn-field-label">EDI-Verortung</label>
            <EdiVerortung anchor={a} />
          </Stack>
        )}

        {bo4eFieldInfo?.found && (
          <Stack style={{ gap: "var(--s-1)" }}>
            <label className="prn-field-label">BO4E-Ziel</label>
            <Bo4eZielDoc info={bo4eFieldInfo} align="left" />
          </Stack>
        )}

        <Flex style={{ gap: "var(--s-4)", flexWrap: "wrap" }}>
          <Stack style={{ gap: "var(--s-1)" }}>
            <label className="prn-field-label" htmlFor="status-select">Status</label>
            <Select
              id="status-select"
              selectedKey={status}
              onSelectionChange={(k) => { if (k) setStatus(String(k)); }}
            >
              {STATUS_VALUES.map((s) => (
                <SelectItem key={s} id={s}>{s}</SelectItem>
              ))}
            </Select>
          </Stack>
        </Flex>

        <Stack style={{ gap: "var(--s-1)" }}>
          <label className="prn-field-label" htmlFor="notes">Notizen</label>
          <textarea
            id="notes"
            className="prn-input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>

        <Flex style={{ gap: "var(--s-3)", alignItems: "center" }}>
          {isCreate ? (
            <Badge tone="blue">neues Entry</Badge>
          ) : (
            <Badge tone="blue">version {base.version}</Badge>
          )}
          <span style={small}>
            Speichern schreibt ins Bundle-Repo (git-Commit als Audit-Trail).
          </span>
        </Flex>
      </Stack>
    );
  }

  // ---- Flow-Band-Daten (EDI-Verortung → Transform → BO4E, in jedem Schritt sichtbar) ----
  const flowTransformType =
    mode === "advanced" ? String(parseAdvanced()?.type ?? base.transform.type) : mode;
  const flowTransformLabel = describeTransform(mode, {
    table: lookupTable,
    dateFormat,
    constValue,
    advancedType: String(parseAdvanced()?.type ?? base.transform.type),
  });
  const flowBo4eType =
    (targetChoice?.kind === "skeleton" ? targetChoice.target.type : undefined) ??
    bo4eFieldInfo?.type ??
    bo4eFieldInfo?.enum_name ??
    undefined;
  const flowBo4eDescription = bo4eFieldInfo?.found
    ? bo4eFieldInfo.description ?? bo4eFieldInfo.enum_description ?? undefined
    : undefined;
  const jumpTo = (n: number) => {
    if (canReach(n)) setStep(n);
  };

  return (
    <Modal
      isOpen
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
      title={isCreate ? "Neues Mapping anlegen" : `Mapping bearbeiten — ${base.mapping_id}`}
      className="prn-dialog-wide"
    >
      <Stack style={{ gap: "var(--s-3)", padding: "var(--s-2) 0", height: "100%", overflow: "auto" }}>
        {/* Stepper: 4 klickbare Schritt-Chips */}
        <Flex style={{ gap: "var(--s-2)", alignItems: "center", flexWrap: "wrap" }}>
          {STEPS.map((s) => (
            <Button
              key={s.n}
              variant={step === s.n ? "filled" : "plain"}
              isDisabled={!canReach(s.n)}
              onPress={() => setStep(s.n)}
              data-testid={`step-${s.n}`}
            >
              {`${s.n} · ${s.title}`}
            </Button>
          ))}
        </Flex>

        {/* Flow-Band: EDI-Verortung → Transform → BO4E, in jedem Schritt sichtbar.
            Jede Zone springt zum passenden Schritt. */}
        <MappingFlowHeader
          anchor={selectedAnchor}
          transformType={flowTransformType}
          transformLabel={flowTransformLabel}
          bo4e={effectiveBo4e}
          bo4eType={flowBo4eType}
          bo4eDescription={flowBo4eDescription}
          direction={direction}
          step={step}
          onJump={jumpTo}
        />

        {conflict && (
          <Notice tone="critical">
            Entry wurde zwischenzeitlich geändert — Dialog schließen und neu öffnen.
          </Notice>
        )}
        {error && (
          <Notice tone="negative">
            {error}
          </Notice>
        )}
        {assistNote && (
          <Notice tone="info">
            KI-Vorschlag (bitte prüfen): {assistNote}
          </Notice>
        )}

        {step === 1 && step1()}
        {step === 2 && step2()}
        {step === 3 && step3()}
        {step === 4 && step4()}
      </Stack>

      {/* Footer-Aktionen */}
      <Flex style={{ gap: "var(--s-2)", justifyContent: "space-between", marginTop: "var(--s-3)", paddingTop: "var(--s-2)", borderTop: "1px solid var(--sapList_BorderColor)" }}>
        <Flex style={{ gap: "var(--s-2)" }}>
          <Button variant="plain" onPress={() => setStep((s) => Math.max(1, s - 1))} isDisabled={step === 1}>
            Zurück
          </Button>
          {!isCreate && (
            <Button
              variant="plain"
              onPress={handleDelete}
              isDisabled={deleting || saving}
              data-testid="delete-btn"
            >
              <Icon name={princeIcon("decline")} size={16} />
              Löschen
            </Button>
          )}
          <Button
            variant="tinted"
            onPress={handleAssist}
            isDisabled={assisting || saving}
            data-testid="assist-btn"
          >
            {assisting ? "KI denkt …" : "KI-Assistenz"}
          </Button>
        </Flex>
        <Flex style={{ gap: "var(--s-2)" }}>
          {step < 4 ? (
            <Button
              variant="filled"
              onPress={() => setStep((s) => Math.min(4, s + 1))}
              isDisabled={!isStepValid(step)}
              data-testid="next-btn"
            >
              Weiter
            </Button>
          ) : (
            <Button variant="filled" onPress={save} isDisabled={saving} data-testid="save-btn">
              Speichern
            </Button>
          )}
          <Button variant="plain" onPress={onClose}>Abbrechen</Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
